import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import Heading from '../../../generic-components/Heading';
import {
  getAnalyticsFlowName,
  getSuccessDetailsBlocks,
  MoveMoneySteps,
  MoveMoneyTypes,
  reviewContentMap,
  successContentMap,
} from '../consts';
import { ReactComponent as DownloadIcon } from '../../../assets/svg/invoices-icons/icon-download.svg';
import { ReactComponent as ApprovedIcon } from '../../../assets/icons/svg/colored-icons/ic-colored-success-new.svg';
import { VIEWS_MAP } from '../../../router/constants';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { NavigationHeader } from '../components/NavigationHeader';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { takeSnapshot } from '../../../services/snapshot.service';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { DefaultFooter } from '../components/success/DefaultFooter';
import { BillNotifyVendorFooter } from '../components/success/BillNotifyVendorFooter';
import { validateFeatureFlag } from '../../../services/featureFlagService';
import { FEATURE_FLAG } from '../../../constants/general';
import { useNotifyVendor } from '../../BillPay/features/PayBill/hooks/useNotifyVendor';
import LiliToast, { LiliToastDefaultConfig } from '../../../DesignSystem/LiliToast/LiliToast';
import LiliNotification, { LiliNotificationType } from '../../../DesignSystem/LiliNotification/LiliNotification';
import { useMoveMoneyLogic } from '../hooks/useMoveMoneyLogic';
import { GENERIC_FILE_UPLOAD_TYPES } from '../../../types';

export const Success = () => {
  const { moveType } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  /** Queries */

  const {
    fetchAchOutLimits,
    fetchWireLimits,
    fetchWireFees,
    fetchMailLimits,
    fetchMailFees,
    isLoadingState,
    resetMoveMoneyData,
    moveMoneyData,
    getMoveMoneyData,
    postUploadSnapShot,
    setMoveMoneyData,
    resetMoveMoneyDataForAnotherTransfer,
    fetchInternationalData,
  } = useMoveMoneyLogic();
  const { refetch: fetchSummaryData } = useAccountSummaryQuery();
  const { mutate: notifyVendor, isLoading: isLoadingNotifyVendor } = useNotifyVendor(
    () => {
      toast(
        <LiliToast
          text={t('MOVE_MONEY.STEPS.SUCCESS.NOTIFY_VENDOR.NOTIFICATION_SEND')}
          id='vendorNotificationSendSuccess'
        />,
        { ...LiliToastDefaultConfig },
      );
      resetMoveMoneyData();
      navigate(`/${VIEWS_MAP.BILLS.BASE}`);
    },
    (errData) => {
      toast(
        <LiliNotification
          type={LiliNotificationType.ERROR}
          text={errData.response.data.errors[0].localizedMessage}
          id='vendorNotificationSendFailure'
        />,
        { ...LiliToastDefaultConfig },
      );
    },
  );
  /** ------- */

  const handleCloseFlow = () => {
    resetMoveMoneyData();
    navigate(`/${VIEWS_MAP.BILLS.BASE}`);
  };

  const handleDone = () => {
    trackMixPanel('Act on Move Money Confirmation Screen', {
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      Action: 'Done',
    });
    resetMoveMoneyData();
    navigate(moveMoneyData?.billId ? `/${VIEWS_MAP.BILLS.BASE}` : `/${VIEWS_MAP.DASHBOARD}`);
  };

  const handleLimits = async () => {
    if (moveType === MoveMoneyTypes.WIRE_OUT) {
      const limitsResponse = await fetchWireLimits();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, limitsData: limitsResponse.data });
    } else if (moveType === MoveMoneyTypes.MAIL_CHECK) {
      const limitsResponse = await fetchMailLimits();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, limitsData: limitsResponse.data });
    } else if (moveType === MoveMoneyTypes.ACH_OUT_TRANSFER) {
      const limitsResponse = await fetchAchOutLimits();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, limitsData: limitsResponse.data });
    }
  };

  const handleFees = async () => {
    if (moveType === MoveMoneyTypes.WIRE_OUT) {
      const feesResponse = await fetchWireFees();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, feeData: feesResponse.data });
    } else if (moveType === MoveMoneyTypes.MAIL_CHECK) {
      const feesResponse = await fetchMailFees();
      const currMoveMoneyData = getMoveMoneyData();
      setMoveMoneyData({ ...currMoveMoneyData, feeData: feesResponse.data });
    }
  };

  const handleShare = () => {
    trackMixPanel('Act on Move Money Confirmation Screen', {
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      Action: 'Share',
    });
    takeSnapshot('snapShotWrapper', true).then((url) => {
      const elLink = document.createElement('a');
      elLink.setAttribute('href', url as string);
      elLink.setAttribute(
        'download',
        `${getAnalyticsFlowName(moveType as MoveMoneyTypes)}-${format(new Date(), 'MMM dd, yyyy')}`,
      );
      elLink.click();
    });
  };

  const handleCombinedData = async () => {
    if (moveType !== MoveMoneyTypes.INTERNATIONAL_WIRE_OUT) return;
    const { data } = await fetchInternationalData();
    const currMoveMoneyData = getMoveMoneyData();
    setMoveMoneyData({
      ...currMoveMoneyData,
      introRequired: data?.introRequired || false,
      eddRequired: data?.eddRequired || false,
      eddVersion: data?.eddVersion || '',
      purposes: data?.purposes || [],
      limitsData: {
        singleLimit: data?.limits.singleLimit,
        dailyLimit: data?.limits.dailyLimit,
        monthlyLimit: data?.limits.monthlyLimit,
        balance: data?.limits.balance || 0,
      },
      feeData: {
        fees: data?.fees || [],
      },
    });
  };

  const handleAnotherTransfer = async () => {
    trackMixPanel('Act on Move Money Confirmation Screen', {
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      Action: 'Send Another',
    });
    await handleCombinedData();
    await handleLimits();
    await handleFees();
    resetMoveMoneyDataForAnotherTransfer();
    setMoveMoneyData({ ...getMoveMoneyData(), activeStepId: MoveMoneySteps.VENDOR_PICKER });
  };

  const onSendVendorNotificationClick = async (newEmail: string) => {
    if (isLoadingNotifyVendor) return;
    trackMixPanel('Act on Notify Vendor Screen', { Action: 'Send Notification' });
    notifyVendor({
      billId: moveMoneyData.billId,
      vendorEmail: newEmail,
    });
  };

  const getFileType = () => {
    switch (moveType) {
      case MoveMoneyTypes.WIRE_OUT:
        return GENERIC_FILE_UPLOAD_TYPES.WIRE_OUT;
      case MoveMoneyTypes.INTERNATIONAL_WIRE_OUT:
        return GENERIC_FILE_UPLOAD_TYPES.INTL_WIRE_OUT;
      case MoveMoneyTypes.ACH_OUT_TRANSFER:
        return GENERIC_FILE_UPLOAD_TYPES.ACH_OUT;
      case MoveMoneyTypes.MAIL_CHECK:
        return GENERIC_FILE_UPLOAD_TYPES.MAIL_A_CHECK;
      default:
        return GENERIC_FILE_UPLOAD_TYPES.WIRE_OUT;
    }
  };

  useEffect(() => {
    postUploadSnapShot({ fileUsageType: getFileType(), imageName: `${Date.now()}` });
    fetchSummaryData();
    trackMixPanel('View Move Money Confirmation Screen', { Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes) });
  }, []);

  const notifyEnabledButNoEmail = !!moveMoneyData.vendorData?.notifyOnPayment && !moveMoneyData.vendorData?.email;
  const isNotifyVendorFeatureEnabled = validateFeatureFlag(FEATURE_FLAG.NOTIFY_VENDOR);
  const shouldNotifyVendor =
    isNotifyVendorFeatureEnabled &&
    moveMoneyData.billId &&
    (!moveMoneyData.vendorData?.notifyOnPayment || notifyEnabledButNoEmail);

  return (
    <div className='absolute left-0 top-0 w-full h-full bg-bw02 z-50 flex flex-col items-center'>
      <NavigationHeader shouldDirectClose hideBackButton onBack={() => {}} onClose={handleCloseFlow} />
      <ApprovedIcon className='mb-[16px]' />
      <Heading
        title={t(successContentMap[moveType as keyof typeof successContentMap].title)}
        className='font-metricRMedium text-[18px] text-bw08'
      />
      <Heading
        title={t(successContentMap[moveType as keyof typeof successContentMap].sub_title)}
        className='font-metricR text-[16px] text-bw07 mb-[16px]'
      />
      <div id='snapShotWrapper' className='rounded-[8px] border border-bw03 w-[432px] bg-bw00 mx-auto mb-[16px]'>
        {/** Header Amount */}
        <div className='flex flex-col items-center justify-center py-[18px] w-full border-b border-bw03'>
          <Heading title={t('MOVE_MONEY.STEPS.SUCCESS.AMOUNT')} className='font-metricR text-[14px] text-bw07' />
          <Heading
            id='TRANSFER_DETAILS_AMOUNT'
            title={convertValueToUSD(moveMoneyData.amount, 2)}
            className='font-metricRMedium text-[26px] text-action03'
          />
        </div>
        {/** Dynamic Data */}
        {getSuccessDetailsBlocks(moveType as keyof typeof reviewContentMap, getMoveMoneyData()).map((block, index) => (
          <div
            key={block.title + index.toString()}
            className='flex items-center justify-between py-[12px] px-[16px] w-full border-b border-bw03'
          >
            <Heading
              title={t(block.title, { defaultValue: block.title })}
              className='font-metricR text-[16px] text-bw07'
            />
            <Heading
              id={block.id || ''}
              title={block.value}
              className='font-metricR text-[14px] text-bw08 break-all text-wrap'
            />
          </div>
        ))}

        <div className='flex items-center rounded-bl-[8px] rounded-br-[8px] justify-center py-[12px] px-[16px] w-full bg-bw01'>
          <Heading
            title={`${t(successContentMap[moveType as keyof typeof successContentMap].move_time_stamp)} ${format(new Date(), 'MMM dd, yyyy - HH:mm')}`}
            className='font-metricR text-[14px] text-bw07'
          />
        </div>
      </div>

      <button
        onClick={handleShare}
        type='button'
        className='flex items-center justify-center border border-bw05 bg-bw00 hover:bg-bw01 px-[20px] rounded-[30px]'
      >
        <LiliButton
          className='w-fit'
          id='shareTransfer'
          title={t('MOVE_MONEY.STEPS.SUCCESS.SHARE')}
          icon={<DownloadIcon />}
          type={LiliButtonType.SKIP}
        />
      </button>

      {/** Footer */}
      <div className='fixed bg-bw00 left-0 bottom-0 w-full border-t border-bw03 flex items-center justify-center py-[24px] gap-[12px]'>
        {shouldNotifyVendor ? (
          <BillNotifyVendorFooter
            onSendVendorNotificationClick={onSendVendorNotificationClick}
            handleDone={handleDone}
          />
        ) : (
          <DefaultFooter
            handleAnotherTransfer={handleAnotherTransfer}
            handleDone={handleDone}
            isLoadingAndDisabled={isLoadingState}
          />
        )}
      </div>
    </div>
  );
};
